/**
 * TvlLegacyParts（design.md参照: LegacyPartsBundle）。
 *
 * レガシー Smarty テンプレートの `data-tvl-component` 要素へ、許可リスト上の
 * Vue コンポーネントをマウントする唯一のエントリポイント。
 *
 * グローバル Vue（ページ側で読み込まれる `/js/vue/vue.min.js`）は上書きしない。
 * このバンドルは自前の Vue（このファイルが import する `vue`）を内包し、
 * 公開するグローバルは window.TvlLegacyParts のみ。
 */
import Vue from 'vue';
import Vuex from 'vuex';
import Header from '~/src_common/components/common/Header.vue';
import Footer from '~/src_common/components/parts/common/Footer.vue';
import CompleteReceiptTable from '~/src_common/components/parts/complete/CompleteReceiptTable.vue';
import CompleteContactInfo from '~/src_common/components/parts/complete/CompleteContactInfo.vue';
import CompleteMypageSection from '~/src_common/components/parts/complete/CompleteMypageSection.vue';
import CompleteContactCta from '~/src_common/components/parts/complete/CompleteContactCta.vue';
import CompleteStepper from '~/src_common/components/parts/complete/CompleteStepper.vue';
import CompleteCrossSellBanner from '~/src_common/components/parts/complete/CompleteCrossSellBanner.vue';
import AppDownloadBanner from '~/src_common/components/parts/complete/AppDownloadBanner.vue';
import Button from '~/src_common/components/parts/button/Button.vue';
import InformationMessage from '~/src_common/components/parts/feedback/InformationMessage.vue';
import AbTreatmentMixin from '~/src_common/mixins/AbTreatmentMixin';
import {
  PAYMENT_FLIGHT_NOTES,
  HOTEL_NOTES,
} from '~/src_common/constants/CompleteScreen';
import { createLegacyStore, defaultRouteStub, RouterLinkStub } from './store';

Vue.use(Vuex);
// CartIcon.vue が使う <router-link> のフォールバック（Vue Routerは積まない）
Vue.component('router-link', RouterLinkStub);

// isDesktopは、マウント対象のトップレベルコンポーネントだけでなく、
// Header.vueがcomponents:{}で内部登録しているGrobalNav/CartIcon/
// HeaderPointModal等のネストした子コンポーネントにも届く必要がある。
// Vueのmixinsオプションは親→子へは伝播しない（子はVueのグローバルベース
// コンストラクタ経由で解決されるため）ため、instance-localなmixinでは
// ネストした子まで届かない。Vue.mixin（グローバル）で対応する。
// 値は「今まさに同期的にマウント中の島」を指すモジュールスコープの変数を
// 読む data() 関数にする。1島のマウント（$mount）はネストした子の生成も
// 含めて完全に同期的に完了するため、島をまたいだ値の混線は起こらない。
let currentIsDesktop = false;
Vue.mixin({
  data() {
    return { isDesktop: currentIsDesktop };
  },
});

const MOUNTED_ATTR = 'data-tvl-mounted';
const MEMBER_ME_URL = '/api/v2/member/me';
const MEMBER_ME_TIMEOUT_MS = 3000;
// buildNamedSlotChildren/mountComponent間で、実DOMノード（DocumentFragment）を
// 一時的に見つけ出すためだけに使う内部マーカー属性。移動後は取り除く。
const NAMED_SLOT_PLACEHOLDER_ATTR = 'data-tvl-slot-placeholder';

// 許可リスト（design.md LegacyComponentName）。ここに無い名前はマウントしない。
const COMPONENTS = {
  Header,
  Footer,
  CompleteReceiptTable,
  CompleteContactInfo,
  CompleteMypageSection,
  CompleteContactCta,
  CompleteStepper,
  Button,
  InformationMessage,
  CompleteCrossSellBanner,
  AppDownloadBanner,
};

const store = createLegacyStore();
// 全島共通の既定 $route（Header.vue のみが参照する）。
// Header 個別の data-tvl-page-name はインスタンス単位のmixinでこれを上書きする。
Vue.prototype.$route = defaultRouteStub;

// Header以外は即時マウント対象外なので、待機列に載るのはHeaderのみ。
const pendingHeaderElements = new Set();
let memberContextPromise = null;
let memberContextSettled = false;

function isAllowedComponent(name) {
  return Object.prototype.hasOwnProperty.call(COMPONENTS, name);
}

function parseProps(el) {
  const raw = el.getAttribute('data-tvl-props');
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch (e) {
    // data-tvl-props には受付番号・メールアドレス等の個人情報が入り得るため、
    // 生の値(raw)はログへ出さない
    console.warn(
      '[TvlLegacyParts] data-tvl-props のJSONパースに失敗しました（空propsでマウントします）',
      e
    );
    return {};
  }
}

function resolveNotes(el) {
  const kind = el.getAttribute('data-tvl-notes-kind');
  if (kind === 'flight') return PAYMENT_FLIGHT_NOTES;
  if (kind === 'hotel') return HOTEL_NOTES;
  console.warn(
    `[TvlLegacyParts] data-tvl-notes-kind が不正です（"flight"/"hotel"のいずれかを指定してください。notesは空配列でマウントします）: ${kind}`
  );
  return [];
}

function navigateToMypage(el) {
  const url = el.getAttribute('data-tvl-mypage-url');
  if (!url) {
    console.warn(
      '[TvlLegacyParts] data-tvl-mypage-url が指定されていません（navigate-to-mypageは無視されました）'
    );
    return;
  }
  location.href = url;
}

/**
 * elの直下の子要素のうち data-tvl-slot-name を持つものを集め、
 * named slotへ流し込むvnodeを組み立てる。マウント（elの中身の破棄）より
 * 前に呼ぶこと。
 *
 * 子要素の中身は innerHTML 文字列としてではなく、実DOMノードそのものを
 * DocumentFragment へ退避して運ぶ（capturedSlotsに積む）。文字列化すると
 * 中に既存の生きたVueインスタンス（例: ページ側スクリプトが同期マウント
 * した #vue-app）が含まれていた場合、そのインスタンスがDOMから切り離され
 * 描画が固まってしまう（reactiveな再描画がもう画面に反映されない）ため。
 * vnode自体は空のプレースホルダーとし、実ノードはmountComponent側で
 * $mount後にプレースホルダーへ appendChild して合流させる。
 */
function buildNamedSlotChildren(el, h, capturedSlots) {
  return Array.from(el.children)
    .filter(child => child.hasAttribute('data-tvl-slot-name'))
    .map(child => {
      const slotName = child.getAttribute('data-tvl-slot-name');
      const fragment = document.createDocumentFragment();
      while (child.firstChild) {
        fragment.appendChild(child.firstChild);
      }
      capturedSlots.push({ slotName, fragment });
      return h('div', {
        slot: slotName,
        attrs: { [NAMED_SLOT_PLACEHOLDER_ATTR]: slotName },
      });
    });
}

/**
 * 単一の data-tvl-component 要素をマウントする。
 * elそのものは破棄せず、内容だけを差し替える（Vueの in-place $mount(el) は
 * el自身をDOMから置換してしまい data-tvl-* 属性ごと失われるため使わない）。
 */
function mountComponent(el, name) {
  if (el.getAttribute(MOUNTED_ATTR) === 'true') return;

  const Component = COMPONENTS[name];
  const props = parseProps(el);
  currentIsDesktop = el.getAttribute('data-tvl-desktop') === 'true';
  const defaultSlotText = el.getAttribute('data-tvl-slot');

  if (name === 'CompleteContactInfo') {
    props.notes = resolveNotes(el);
  }

  if (name === 'Footer') {
    // SPA側 App.vue の <Footer :visible="!isNative"> と同形。
    // アプリ内WebViewでは共通可視フッターを表示しない（要件3.2/10.3）。
    props.visible = !store.state.isNative;
  }

  let MountedComponent = Component;
  if (name === 'Header') {
    // Header.vue自身のscoped SCSSは、SPドロワー（.nav2/.opened等）を
    // 「.sp 祖先クラスの子孫」としてのみ定義しており、通常のSPA文脈では
    // 上位レイアウトがbodyへ`sp`/`pc`を付与する前提になっている。レガシー
    // マウントではその祖先が存在しないため、ここで明示的に付与しないと
    // ドロワーの開閉スタイル（オフスクリーン配置）が一切適用されない。
    document.body.classList.toggle('sp', !currentIsDesktop);
    document.body.classList.toggle('pc', currentIsDesktop);
    const pageName = el.getAttribute('data-tvl-page-name') || '';
    // $route は Vue の予約プレフィックス（$/_）のため data() では注入できない。
    // beforeCreate でインスタンス自身に直接代入し、prototype既定値を
    // このインスタンス（Header自身。$routeを参照するのはHeader.vueだけで
    // GrobalNav等の子は参照しないため、これはネストの問題にならない）に
    // 限りshadowする。
    MountedComponent = {
      extends: Component,
      beforeCreate() {
        this.$route = { name: pageName };
      },
    };
  }

  if (name === 'CompleteCrossSellBanner') {
    // CompleteCrossSellBanner自体は表示可否の判定ロジックを持たない
    // （タスク2.5）ため、store.crossSell（setCrossSellDataが更新する非同期値）
    // を購読して show=false/null のとき何も描画しない薄いラッパーで包む。
    // マウント自体は他の島と同様に同期・即時（Headerのような保留列は不要）。
    //
    // 実際に描画される子インスタンスは setCrossSellData 後の非同期な
    // 再描画時（store.crossSell.show===true になった時点）に初めて生成される。
    // その時点の currentIsDesktop はグローバル mixin 経由で「最後に同期
    // マウントされた別の島」の値に化けている可能性があるため、このラッパー
    // 自身が同期的にマウントされる「今」の値をクロージャで固定し、mixin
    // （data()）を上書きして子へ渡す。
    const capturedIsDesktop = currentIsDesktop;
    const ComponentWithFixedIsDesktop = {
      extends: Component,
      mixins: [{ data: () => ({ isDesktop: capturedIsDesktop }) }],
    };
    MountedComponent = {
      render(h) {
        const crossSell = this.$store.state.crossSell;
        if (!crossSell || !crossSell.show) return null;
        return h(ComponentWithFixedIsDesktop, {
          props: {
            discountPercent: crossSell.discountPercent,
            linkUrl: crossSell.linkUrl,
            linkText: crossSell.linkText,
            catchText: crossSell.catchText,
          },
        });
      },
    };
  }

  if (name === 'AppDownloadBanner') {
    // AbTreatmentMixin（design.md「AppDownloadBannerのマウント動作」、要件14.7）。
    // getCurrentPid()をApplicationComplete.vueと同一の'mypage'に固定し、
    // $route.query.ab（defaultRouteStub経由、ページ読み込み時に1度だけ解決）で
    // 予約完了画面と同一のAB非表示判定を再現する。
    // アプリ内WebViewでは表示しない（要件14.3。AppDownloadBanner.vue自身は
    // 判定ロジックを持たず呼び出し元制御のため、ここでisNativeを見る）。
    MountedComponent = {
      mixins: [AbTreatmentMixin],
      methods: {
        getCurrentPid() {
          return 'mypage';
        },
      },
      render(h) {
        if (this.shouldHideForAbTreatment || this.$store.state.isNative)
          return null;
        return h(Component);
      },
    };
  }

  const capturedSlots = [];
  const vm = new Vue({
    store,
    render(h) {
      const children = [];
      if (defaultSlotText != null) {
        children.push(defaultSlotText);
      }
      children.push(...buildNamedSlotChildren(el, h, capturedSlots));

      const data = { props };
      if (name === 'CompleteMypageSection') {
        data.on = {
          'navigate-to-mypage': () => navigateToMypage(el),
        };
      }
      return h(MountedComponent, data, children);
    },
  }).$mount();

  // 退避した実DOMノード（生きたVueインスタンスを含み得る）を、レンダリング
  // 済みのプレースホルダーへ合流させる。この時点ではvm.$elはまだ文書に
  // 未接続だが、DOM移動自体はノードの接続状態に依存しないため問題ない。
  capturedSlots.forEach(({ slotName, fragment }) => {
    const placeholder = vm.$el.querySelector(
      `[${NAMED_SLOT_PLACEHOLDER_ATTR}="${slotName}"]`
    );
    if (!placeholder) return;
    placeholder.removeAttribute(NAMED_SLOT_PLACEHOLDER_ATTR);
    placeholder.appendChild(fragment);
  });

  el.innerHTML = '';
  el.appendChild(vm.$el);
  el.setAttribute(MOUNTED_ATTR, 'true');
}

function fetchMemberContext() {
  return new Promise(resolve => {
    let settled = false;
    // AbortControllerが無い環境ではsetTimeoutへ到達せずcatchへ抜けるため、
    // finalizeLoggedOut()がTDZに触れないようconstではなくlet+nullで持つ
    let timeoutId = null;

    const finalizeLoggedOut = () => {
      if (settled) return;
      settled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
      store.commit('member/setUser', null);
      resolve();
    };

    try {
      const controller = new AbortController();

      timeoutId = setTimeout(() => {
        controller.abort();
        finalizeLoggedOut();
      }, MEMBER_ME_TIMEOUT_MS);

      fetch(MEMBER_ME_URL, {
        credentials: 'include',
        signal: controller.signal,
      })
        .then(res => (res && res.ok ? res.json() : null))
        .then(json => {
          if (settled) return; // タイムアウトで既に確定済み。遅延応答は無視する
          settled = true;
          clearTimeout(timeoutId);
          if (json && json.code === 200 && json.data) {
            store.commit('member/setUser', json.data);
          } else {
            store.commit('member/setUser', null);
          }
          resolve();
        })
        .catch(() => {
          finalizeLoggedOut();
        });
    } catch (e) {
      // AbortController/fetchが無い環境（古いAndroid WebView等）ではexecutorが
      // 同期throwし、rejectしたpromiseのせいでpendingHeaderElementsがflushされず
      // ヘッダーが出ないまま止まる。未ログイン扱いで確定させてフェイルオープンする
      finalizeLoggedOut();
    }
  });
}

/**
 * 指定範囲（既定はdocument）のdata-tvl-component要素をマウントする。
 * 二重マウントはしない（design.md「Header のマウント機構」1〜4）。
 */
function mount(root = document) {
  const elements = Array.from(root.querySelectorAll('[data-tvl-component]'));
  const newlyPendingHeaders = [];

  elements.forEach(el => {
    if (el.getAttribute(MOUNTED_ATTR) === 'true') return;

    const name = el.getAttribute('data-tvl-component');
    if (!isAllowedComponent(name)) {
      console.warn(
        `[TvlLegacyParts] 許可リスト外のコンポーネントです（マウントをスキップします）: ${name}`
      );
      return;
    }

    if (name === 'Header') {
      if (store.state.isNative) {
        // アプリ内WebViewではHeader.vueのルート要素がv-if="!isNative"で
        // 消え、描画結果が会員状態に依存しない。会員API解決を待つと
        // マウント点のプレースホルダ（PC 60px / SP 94px の空header）が
        // 最大MEMBER_ME_TIMEOUT_MS分残ってから畳まれ、コンテンツが
        // 跳ねてしまうため、待たずに即時マウントする。
        mountComponent(el, name);
        return;
      }

      if (pendingHeaderElements.has(el)) return; // 既に保留列に積まれている

      if (memberContextSettled) {
        // 会員コンテキストが既に確定済みなら、後から見つかったHeaderも即時マウントしてよい
        mountComponent(el, name);
      } else {
        pendingHeaderElements.add(el);
        newlyPendingHeaders.push(el);
      }
      return;
    }

    mountComponent(el, name);
  });

  if (newlyPendingHeaders.length > 0 && !memberContextPromise) {
    // fetchMemberContext()自身もフェイルオープンするが、想定外のrejectで
    // 保留中のHeaderが永久にマウントされない事態だけは避ける（157テンプレートに影響）
    memberContextPromise = fetchMemberContext()
      .catch(() => {})
      .then(() => {
        memberContextSettled = true;
        const toMount = Array.from(pendingHeaderElements);
        pendingHeaderElements.clear();
        toMount.forEach(el => mountComponent(el, 'Header'));
      });
  }
}

/**
 * ページ側スクリプトからstore shimの値の一部を上書きする（design.md参照）。
 * store.js が公開しているミューテーションの範囲でのみ対応する
 * （isFromMetaSearch以外はmember APIの結果としてのみ更新される値のため、
 * ここから直接setUserを叩くAPIは公開しない）。
 */
function setMemberContext(patch) {
  if (!patch || typeof patch !== 'object') return;

  if (Object.prototype.hasOwnProperty.call(patch, 'isFromMetaSearch')) {
    store.commit('setIsFromMetaSearch', !!patch.isFromMetaSearch);
  }

  const unsupportedKeys = Object.keys(patch).filter(
    key => key !== 'isFromMetaSearch'
  );
  if (unsupportedKeys.length > 0) {
    console.warn(
      `[TvlLegacyParts] setMemberContext: 未対応のキーです（無視されました）: ${unsupportedKeys.join(
        ', '
      )}`
    );
  }
}

/**
 * payment_completion_screen.js がクロスセル訴求バナーの表示要否・データ確定時に呼ぶ
 * ブリッジ（design.md CrossSellData / 要件13.4）。setMemberContextと同じパターンで
 * store.crossSell stateをそのまま更新し、CompleteCrossSellBannerインスタンスが
 * 購読して再描画する。
 */
function setCrossSellData(data) {
  store.commit('setCrossSellData', data);
}

const TvlLegacyParts = { mount, setMemberContext, setCrossSellData };

if (typeof window !== 'undefined') {
  window.TvlLegacyParts = TvlLegacyParts;
}

function bootstrap() {
  mount();
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
}

export default TvlLegacyParts;
